/**
 * Los tres estados de un producto, y cuál manda sobre cuál.
 *
 * 🪤 QUÉ HABÍA ANTES: "archivado" se deducía de `is_active = false AND show_in_store = true`. Un
 * interruptor de CANAL usado para representar una etapa del CICLO DE VIDA — y al revés de lo que la
 * palabra significa, porque dejaba el producto visible en la tienda. Las pestañas "Borradores" y
 * "Archivados" de la lista terminaban filtrando por lo mismo, así que mostraban idéntico contenido.
 *
 * Ahora hay una columna propia y un orden de precedencia explícito:
 *
 *     archivado → archived_at != null      (gana sobre todo)
 *     activo    → no archivado Y is_active
 *     borrador  → no archivado Y !is_active
 *
 * Que archivado GANE es la decisión importante: un producto archivado no es "un activo raro" ni "un
 * borrador viejo". Salió del catálogo y se guarda por su historia.
 */

export type EstadoProducto = 'activo' | 'borrador' | 'archivado';

export interface CamposDeEstado {
  is_active: boolean;
  archived_at: string | null;
}

export function estadoDeProducto(p: CamposDeEstado): EstadoProducto {
  if (p.archived_at) return 'archivado';
  return p.is_active ? 'activo' : 'borrador';
}

/** Cómo se llama cada estado en pantalla. Una sola vez, para que no se dupliquen los nombres. */
export const ETIQUETA_ESTADO: Record<EstadoProducto, string> = {
  activo: 'Activo',
  borrador: 'Borrador',
  archivado: 'Archivado',
};

/**
 * Los cambios que hay que escribir para archivar.
 *
 * Apaga TAMBIÉN `is_active` y `show_in_store` a propósito: así el producto desaparece de todos los
 * canales usando la lógica que ya existe, en vez de tener que enseñarle el concepto "archivado" a
 * cada consulta —incluida la del Worker de la tienda, que vive en otro repositorio y se despliega
 * aparte—. Un estado nuevo que exige tocar cinco lugares para que se respete no es un estado: es
 * una promesa.
 */
export function cambiosParaArchivar(ahora: Date): Record<string, unknown> {
  return { archived_at: ahora.toISOString(), is_active: false, show_in_store: false };
}

/**
 * Y los de desarchivar. Vuelve como BORRADOR, no como activo: sacarlo del archivo no debería
 * republicarlo solo. Que vuelva a venderse es una decisión aparte, del vendedor.
 */
export function cambiosParaDesarchivar(): Record<string, unknown> {
  return { archived_at: null, is_active: false };
}

/** ¿Esta fila entra en la vista pedida? Las vistas normales NUNCA muestran archivados. */
export function coincideConVista(p: CamposDeEstado, vista: 'all' | 'active' | 'draft' | 'archived'): boolean {
  const estado = estadoDeProducto(p);
  if (vista === 'archived') return estado === 'archivado';
  if (estado === 'archivado') return false;
  if (vista === 'active') return estado === 'activo';
  if (vista === 'draft') return estado === 'borrador';
  return true;
}
